import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBase } from "../../utils/API/fetchBaseQuery";
import { transformRequest } from "../../utils/API/requestMiddleware";
import { transformErrorResponse, transformSuccessResponse } from "../../utils/API/responseMiddleware";

const financialsAPI = createApi({
    reducerPath: 'financialsAPI',
    baseQuery: customFetchBase,
    endpoints: (builder) => ({

        getAllOutstandingInvoicesList: builder.mutation({
            query: (data) => ({
                url: `/Financials/GetOutstandingInvoicesList`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getOutstandingInvoiceById: builder.query({
            query: (plInvoiceKey) => ({
                url: `/Financials/GetOutstandinInvoiceById?plInvoiceKey=${plInvoiceKey}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        updateInvoiceStatusAndFollowupDate: builder.mutation({
            query: (data) => ({
                url: `/Financials/UpdateInvoiceStatusAndFollowUpDate`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // Endpoint to get all GLAccounts list
        getAllGLAccounts: builder.query({
            query: () => ({
                url: `/Financials/GetAllGLAccounts`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        updateGLAccounts: builder.mutation({
            query: (data) => ({
                url: `/Financials/UpdateGLAccounts`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        //get all Client On Hold List
        getAllClientsOnHold: builder.mutation({
            query: (data) => ({
                url: `/Financials/GetAllClientsOnHold`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        //get all Work Orders On Hold List
        getAllWorkOrdersOnHold: builder.mutation({
            query: (data) => ({
                url: `/Financials/GetAllWorkOrdersOnHold`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getAllInvoicePayments: builder.mutation({
            query: (data) => ({
                url: `/Financials/GetAllInvoicePayments`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getAllDraftInvoices: builder.mutation({
            query: (data) => ({
                url: `/Financials/GetAllDraftInvoices`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),


        exportOutstandingInvoices: builder.mutation({
            query: (data) => ({
                url: `/Financials/ExportOutstandingInvoices`,
                method: 'POST',
                body: transformRequest(data),
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        exportClientsOnHold: builder.mutation({
            query: (data) => ({
                url: `/Financials/ExportClientsOnHold`,
                method: 'POST',
                body: transformRequest(data),
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        exportWorkOrdersOnHold: builder.mutation({
            query: (data) => ({
                url: `/Financials/ExportWorkOrdersOnHold`,
                method: 'POST',
                body: transformRequest(data),
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        exportInvoicePayments: builder.mutation({
            query: (data) => ({
                url: `/Financials/ExportInvoicePayments`,
                method: 'POST',
                body: transformRequest(data),
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        exportDraftInvoices: builder.mutation({
            query: (data) => ({
                url: `/Financials/ExportDraftInvoices`,
                method: 'POST',
                body: transformRequest(data),
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        importOutstandingInvoices: builder.mutation({
            query: (data) => ({
                url: `/Financials/ImportOutstandingInvoices`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        deleteDraftInvoice: builder.mutation({
            query: (plInvoiceKey) => ({
                url: `/Financials/DeleteDraftInvoice?plInvoiceKey=${plInvoiceKey}`,
                method: 'DELETE',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        clientUpdateOnHold: builder.mutation({
            query: (data) => ({
                url: `/Financials/clientUpdateOnHold`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        workOrderUpdateOnHold: builder.mutation({
            query: (data) => ({
                url: `/Financials/WorkOrderUpdateOnHold`,
                method: 'POST',
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        deleteInvoicePayment: builder.mutation({
            query: (plInvoicePaymentID) => ({
                url: `Financials/DeleteInvoicePayment?plInvoicePaymentID=${plInvoicePaymentID}`,
                method: 'DELETE',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

    })
})

export const {
    useGetAllOutstandingInvoicesListMutation,
    useLazyGetOutstandingInvoiceByIdQuery,
    useUpdateInvoiceStatusAndFollowupDateMutation,
    useLazyGetAllGLAccountsQuery,
    useUpdateGLAccountsMutation,
    useGetAllClientsOnHoldMutation,
    useGetAllWorkOrdersOnHoldMutation,
    useGetAllInvoicePaymentsMutation,
    useGetAllDraftInvoicesMutation,
    useExportOutstandingInvoicesMutation,
    useExportClientsOnHoldMutation,
    useExportWorkOrdersOnHoldMutation,
    useExportInvoicePaymentsMutation,
    useExportDraftInvoicesMutation,
    useImportOutstandingInvoicesMutation,
    useDeleteDraftInvoiceMutation,
    useClientUpdateOnHoldMutation,
    useWorkOrderUpdateOnHoldMutation,
    useDeleteInvoicePaymentMutation,
} = financialsAPI;

export default financialsAPI;